/**
 * teamAdapter.js — Single normalization layer for team members.
 *
 * Both Settings → Team AND Settings → Roles render from the SAME
 * `/api/team/members` payload. This module guarantees they produce the
 * same derived field shape so the two tabs can never drift.
 *
 * The vendor AriaOne payload and the local backend payload differ slightly:
 *
 *   vendor (ehi.ariaone.ai) field → normalized field
 *     user_type: "medical_provider"         → user_type, category:'medical'
 *     user_type: "non_medical"              → user_type, category:'nonMedical'
 *     non_medical_role: "front_desk"        → non_medical_role, role (if role empty)
 *     is_team_owner: true                   → is_team_owner
 *     is_active: true                       → is_active, status ('active'|'invited')
 *     is_schedulable: true                  → is_schedulable, schedulable
 *     npi: "1234567890"                     → npi
 *     role: "physician"                     → role, category:'medical' (inferred)
 *
 * Local (native backend) fields:
 *     category, schedulable, status, apps, assigned_to — mapped forward.
 *
 * The adapter NEVER drops input fields that downstream consumers rely on;
 * it only adds normalized aliases.
 */

/* ── role classification ─────────────────────────────────────────────── */

const MEDICAL_ROLES = new Set(['physician', 'np', 'pa', 'rn', 'ma']);

export const isMedicalRole = (roleId) => MEDICAL_ROLES.has(roleId);

export const roleCategoryFor = (roleId) => (isMedicalRole(roleId) ? 'medical' : 'nonMedical');

/* ── single-record normalization ─────────────────────────────────────── */

/**
 * Normalize a raw team member record (vendor or local) into the canonical
 * shape consumed by UI. Unknown fields are preserved on the returned
 * object; normalized aliases are layered on top.
 *
 * @param {Object} raw
 * @returns {Object} normalized member
 */
export const normalizeMember = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const id = raw.id || raw._id || raw.member_id || raw.user_id || null;
  // Vendor emits `name_display` for invited members and sometimes omits
  // `name` entirely for the team owner — fall back to email local-part.
  const name =
    raw.name || raw.name_display || raw.display_name || raw.full_name ||
    (raw.email ? String(raw.email).split('@')[0] : '') || '';
  const email = (raw.email || raw.user_email || '').toLowerCase();

  // ── role + non_medical_role ──
  // Vendor separates `role` (medical) from `non_medical_role` (ops).
  // Local backend unifies into a single `role` field. Prefer the vendor
  // split when present; fall back to the unified `role` and classify.
  const vendorRole = raw.role || null;
  const nonMedicalRole = raw.non_medical_role || raw.nonMedicalRole || null;
  const effectiveRole = vendorRole || nonMedicalRole || '';

  // ── user_type + category ──
  // Priority: explicit vendor `user_type` → explicit `category` → inferred.
  // INPUT: we accept both the AriaOne §14 spelling (`"medical"` /
  // `"non-medical"`) and the legacy / local spelling (`"medical_provider"`
  // / `"non_medical"`).
  // OUTPUT: we always emit the canonical UI model (`"medical_provider"` /
  // `"non_medical"`) so downstream consumers (Scheduler, Reports) have a
  // single stable shape to switch on.
  const rawUserType = raw.user_type || null;
  const rawUserTypeLc = rawUserType ? String(rawUserType).toLowerCase() : null;
  let category = raw.category || null;
  if (!category && rawUserTypeLc) {
    category = (rawUserTypeLc === 'medical' || rawUserTypeLc === 'medical_provider')
      ? 'medical'
      : 'nonMedical';
  }
  // Final fallback: classify from the effective role.
  if (!category) {
    category = roleCategoryFor(effectiveRole);
  }
  // Emit canonical UI values regardless of input shape.
  const userType = category === 'medical' ? 'medical_provider' : 'non_medical';

  // ── is_active + status ──
  const isActive = raw.is_active !== undefined
    ? !!raw.is_active
    : (raw.status ? raw.status !== 'inactive' && raw.status !== 'disabled' : true);
  const status = raw.status || (isActive ? 'active' : 'inactive');

  // ── is_schedulable + schedulable ──
  const isSchedulable = raw.is_schedulable !== undefined
    ? !!raw.is_schedulable
    : (raw.schedulable !== undefined
        ? !!raw.schedulable
        : (category === 'medical' && MEDICAL_ROLES.has(effectiveRole) && effectiveRole !== 'rn' && effectiveRole !== 'ma'));

  return {
    ...raw,
    id,
    name,
    email,
    role: effectiveRole,
    non_medical_role: nonMedicalRole,
    is_team_owner: !!(raw.is_team_owner || raw.team_owner),
    is_active: isActive,
    is_schedulable: isSchedulable,
    npi: raw.npi || raw.associated_npi || null,
    npi_name: raw.associated_npi_name || null,
    user_type: userType,
    // legacy aliases used by existing render code — keep them writeable by
    // downstream mutations (updates/optimistic patches).
    category,
    schedulable: isSchedulable,
    status,
    specialty: raw.specialty || null,
    assigned_to: raw.assigned_to || raw.assignedTo || null,
    apps: raw.apps || {},
    avatar: raw.avatar || (name ? name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() : ''),
  };
};

export const normalizeMembers = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeMember).filter((m) => m && m.id);
};

/* ── grouping helpers used by the Roles tab ──────────────────────────── */

/** Group members by medical vs non-medical classification. */
export const groupByCategory = (members) => {
  const medical = [];
  const nonMedical = [];
  for (const m of members) {
    if (m.category === 'medical') medical.push(m);
    else nonMedical.push(m);
  }
  return { medical, nonMedical };
};

/** Index members by role id → array (for the Roles tab's per-role chips). */
export const groupByRole = (members) => {
  const byRole = {};
  for (const m of members) {
    const key = m.role || 'unassigned';
    if (!byRole[key]) byRole[key] = [];
    byRole[key].push(m);
  }
  return byRole;
};

/** Members currently eligible to appear in the Scheduler provider list. */
export const schedulableProviders = (members) =>
  members.filter((m) => m.is_schedulable && m.is_active && m.category === 'medical');
